import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Clock, CheckCircle, Award, BookOpen, ArrowLeft, AlertCircle, Loader2, CreditCard, Layers,
  UserCheck, ListChecks, FileCheck, TrendingUp
} from 'lucide-react';
import { COURSES } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import { normalizeCourse } from '../utils/normalizeCourse';
import { getInstalmentPlan } from '../utils/instalments';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function interpolate(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.split(`{${key}}`).join(String(value ?? '')),
    template
  );
}

function formatPrice(price, language) {
  const numericPrice = Number(price) || 0;
  const locale = language === 'ar' ? 'ar-EG' : 'en-GB';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: numericPrice % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericPrice);
}


export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useSiteLanguage();
  const paymentStatus = new URLSearchParams(location.search).get('payment');
  const [course, setCourse] = useState(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Try API first, fall back to mock
  useEffect(() => {
    axios.get(`${API}/courses/${slug}`)
      .then(res => setCourse(normalizeCourse(res.data)))
      .catch(() => {
        const found = COURSES.find(c => c.slug === slug);
        setCourse(normalizeCourse(found));
      })
      .finally(() => setCourseLoading(false));
  }, [slug]);

  const [paymentType, setPaymentType] = useState('full');
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (courseLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <AlertCircle size={48} className="text-gray-300" />
        <p className="text-gray-500">{t('course.notFound', 'Course not found.')}</p>
        <Link to="/courses" className="text-blue-600 hover:underline">{t('course.backToCourses', 'Back to Courses')}</Link>
      </div>
    );
  }

  const hasDiscount = course.onSale && course.originalPrice !== null;
  const instalmentPlan = getInstalmentPlan(course);
  const perInstallment = instalmentPlan ? instalmentPlan.perInstalment : null;
  const installmentTotal = instalmentPlan ? instalmentPlan.total : null;
  const activePrice = paymentType === 'installment' && perInstallment ? perInstallment : course.price;

  const related = COURSES.filter(c => c.category === course.category && c.id !== course.id).slice(0, 3);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/enquiries`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || `Enrolment enquiry for: ${course.title}`,
        course_title: course.title,
        course_id: course.id || course.slug,
        type: 'enrolment',
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(t('apply.errorSubmission', 'Submission failed. Please check your details and try again.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!course) {
      return;
    }

    setPaymentError('');
    setPaying(true);

    try {
      const response = await axios.post(`${API}/payments/checkout-session`, {
        course_slug: course.slug,
        payment_type: paymentType,
        success_url: `${window.location.origin}/courses/${course.slug}?payment=success`,
        cancel_url: `${window.location.origin}/courses/${course.slug}?payment=cancelled`,
        customer_email: formData.email || undefined,
      });

      if (response.data?.checkout_url) {
        window.location.assign(response.data.checkout_url);
        return;
      }

      setPaymentError(t('course.paymentError', 'Unable to start secure payment right now. Please try again shortly.'));
    } catch (err) {
      setPaymentError(err?.response?.data?.detail || t('course.paymentError', 'Unable to start secure payment right now. Please try again shortly.'));
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title={course.title}
        description={course.description}
        path={`/courses/${course.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: course.title,
          description: course.description,
          provider: { '@type': 'EducationalOrganization', name: 'Atlas Academy', url: 'https://www.atlas-academy.co.uk' },
          offers: { '@type': 'Offer', price: course.price, priceCurrency: 'GBP', category: 'Paid' },
          hasCourseInstance: { '@type': 'CourseInstance', courseMode: 'online' },
        }}
      />
      {/* Billboard banner — FL style */}
      <div className="relative bg-gradient-to-br from-[#4B286D] via-[#5a2f82] to-[#3B1D57] text-white overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-[480px] h-[480px] bg-white/5 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#DE00A5]/10 rounded-full -translate-x-1/4 translate-y-1/3 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 py-10 lg:py-14">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">

            {/* Left: text content */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-5 transition-colors"
              >
                <ArrowLeft size={15} /> {t('course.backToCourses', 'Back to Courses')}
              </button>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="bg-white/15 text-white/90 text-xs px-3 py-1 rounded-full font-medium">{course.level}</span>
                <span className="bg-white/15 text-white/90 text-xs px-3 py-1 rounded-full font-medium">{course.category}</span>
                <span className="bg-[#DE00A5]/80 text-white text-xs px-3 py-1 rounded-full font-semibold">{course.awardingBody}</span>
              </div>

              <h1 className="text-2xl lg:text-4xl font-bold mb-3 leading-tight max-w-2xl">{course.title}</h1>
              <p className="text-white/70 max-w-xl text-sm leading-relaxed mb-6 line-clamp-3">{course.description}</p>

              <div className="flex flex-wrap gap-5 text-sm">
                <div className="flex items-center gap-1.5 text-white/80">
                  <Clock size={15} /> {course.duration}
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                  <Award size={15} /> {t('course.detail.certificateValue', 'Official UK Qualification')}
                </div>
                <div className="flex items-center gap-1.5 text-white/80">
                  <BookOpen size={15} /> {t('course.detail.studyModeValue', 'Online / Distance Learning')}
                </div>
              </div>
            </div>

            {/* Right: course image */}
            {course.image && (
              <div className="shrink-0 w-full lg:w-80 xl:w-96">
                <div className="rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/10 aspect-video lg:aspect-[4/3]">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {paymentStatus === 'success' && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            <CheckCircle size={16} /> {t('course.paymentSuccess', 'Payment completed successfully. Your enrolment team will verify the next steps shortly.')}
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <AlertCircle size={16} /> {t('course.paymentCancelled', 'Payment was cancelled. You can resume checkout whenever you are ready.')}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About this course */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-3">{t('course.aboutTitle', 'About This Course')}</h2>
              <p className="text-gray-600 leading-relaxed text-sm">{course.description}</p>
              <p className="text-gray-600 leading-relaxed text-sm mt-3">
                {t('course.aboutSupplement', 'This qualification is fully delivered online through our secure student portal. You will have access to all study materials, assessments, and tutor feedback from day one of enrolment. There are no fixed timetables — you simply study around your life.')}
              </p>
            </div>

            {/* What you will learn */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('course.studyTitle', 'What You Will Study')}</h2>
              <div className="space-y-2">
                {course.modules.map((mod, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                    <span className="text-sm text-gray-700">{mod}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Who is this course for */}
            {course.whoIsFor && (
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <UserCheck size={16} className="text-blue-700" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t('course.whoForTitle', 'Who Is This Course For?')}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">{course.whoIsFor}</p>
              </div>
            )}

            {/* Entry requirements */}
            {course.entryRequirements.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ListChecks size={16} className="text-blue-700" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t('course.entryTitle', 'Entry Requirements')}</h2>
                </div>
                <ul className="space-y-2.5">
                  {course.entryRequirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CheckCircle size={16} className="text-green-600 shrink-0 mt-0.5" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Assessment */}
            {course.assessment && (
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileCheck size={16} className="text-blue-700" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t('course.assessmentTitle', 'How You Will Be Assessed')}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">{course.assessment}</p>
              </div>
            )}

            {/* Career & university progression */}
            {course.progression && (
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp size={16} className="text-blue-700" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{t('course.progressionTitle', 'Career & University Progression')}</h2>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm">{course.progression}</p>
              </div>
            )}

            {/* Key facts */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('course.detailsTitle', 'Course Details')}</h2>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: t('course.detail.level', 'Level'), value: course.level },
                  { label: t('course.detail.duration', 'Duration'), value: course.duration },
                  { label: t('course.detail.studyMode', 'Study Mode'), value: t('course.detail.studyModeValue', 'Online / Distance Learning') },
                  { label: t('course.detail.awardingBody', 'Awarding Body'), value: course.awardingBody },
                  { label: t('course.detail.assessment', 'Assessment'), value: t('course.detail.assessmentValue', 'Portfolio / Written Assignments') },
                  { label: t('course.detail.certificate', 'Certificate'), value: t('course.detail.certificateValue', 'Official UK Qualification') },
                ].map(fact => (
                  <div key={fact.label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{fact.label}</p>
                    <p className="text-sm font-semibold text-gray-800">{fact.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment options */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('course.paymentOptions', 'Payment Options')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Full payment */}
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={15} className="text-blue-600" />
                    <span className="text-sm font-semibold text-blue-900">Pay in Full</span>
                  </div>
                  <p className="text-2xl font-extrabold text-blue-800">£{formatPrice(course.price, language)}</p>
                  <p className="text-xs text-blue-600 mt-1">Single payment — no extras</p>
                </div>

                {/* Instalment */}
                {course.installmentEnabled && perInstallment && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers size={15} className="text-purple-600" />
                      <span className="text-sm font-semibold text-purple-900">2 Instalments</span>
                    </div>
                    <p className="text-2xl font-extrabold text-purple-800">£{formatPrice(perInstallment, language)} <span className="text-base font-semibold">× 2</span></p>
                    <p className="text-xs text-purple-600 mt-1">£{formatPrice(installmentTotal, language)} total</p>
                  </div>
                )}
              </div>
              {course.installmentEnabled && perInstallment && (
                <p className="text-xs text-gray-400 mt-3">
                  Instalments are subject to a £100 spread fee. Second instalment is due 30 days after enrolment.
                </p>
              )}
            </div>
          </div>

          {/* Enrolment sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-24">
              {/* Price header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-extrabold text-blue-700">
                    £{formatPrice(paymentType === 'installment' && perInstallment ? perInstallment : course.price, language)}
                    {paymentType === 'installment' && perInstallment && (
                      <span className="text-base font-semibold text-blue-500"> × 2</span>
                    )}
                  </span>
                  {hasDiscount && paymentType === 'full' && (
                    <span className="text-gray-400 line-through text-sm">£{formatPrice(course.originalPrice, language)}</span>
                  )}
                </div>
                {paymentType === 'installment' && perInstallment ? (
                  <p className="text-purple-600 text-xs font-medium">
                    £{formatPrice(installmentTotal, language)} total — spread over 2 payments
                  </p>
                ) : hasDiscount ? (
                  <p className="text-green-600 text-xs font-medium">
                    {interpolate(t('course.youSave', 'You save £{amount}'), { amount: formatPrice(course.originalPrice - course.price, language) })}
                  </p>
                ) : null}
              </div>

              {/* Payment type selector */}
              {course.installmentEnabled && perInstallment && (
                <div className="p-4 border-b border-gray-100 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Method</p>
                  <label className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${paymentType === 'full' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentType" value="full" checked={paymentType === 'full'} onChange={() => setPaymentType('full')} className="accent-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">Pay in Full</p>
                      <p className="text-xs text-gray-500">£{formatPrice(course.price, language)} — one payment</p>
                    </div>
                    <CreditCard size={14} className="text-blue-500 shrink-0" />
                  </label>
                  <label className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${paymentType === 'installment' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="paymentType" value="installment" checked={paymentType === 'installment'} onChange={() => setPaymentType('installment')} className="accent-purple-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">2 Instalments</p>
                      <p className="text-xs text-gray-500">£{formatPrice(perInstallment, language)} × 2 = £{formatPrice(installmentTotal, language)}</p>
                    </div>
                    <Layers size={14} className="text-purple-500 shrink-0" />
                  </label>
                </div>
              )}

              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={14} className="text-blue-500" /> {course.duration} {t('course.toComplete', 'to complete')}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <BookOpen size={14} className="text-blue-500" /> {course.modules.length} {t('course.modulesCount', 'course modules')}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Award size={14} className="text-blue-500" /> {t('course.accreditedBy', 'Accredited by')} {course.awardingBody}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle size={14} className="text-blue-500" /> {t('course.flexiblePayment', 'Flexible payment plan available')}
                </div>
              </div>
              {!showEnrollForm ? (
                <div className="p-5 pt-0 space-y-2">
                  <Link
                    to={`/apply?course=${course.slug}&payment_type=${paymentType}`}
                    data-testid="course-detail-enrol-cta"
                    className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold text-sm transition-colors"
                  >
                    {t('course.enrolNow', 'Enrol Now')} &mdash; £{formatPrice(activePrice, language)}
                    {paymentType === 'installment' && perInstallment ? ' × 2' : ''}
                  </Link>
                  <button
                    type="button"
                    onClick={handleCheckout}
                    disabled={paying}
                    className="block w-full text-center border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60 py-3 rounded-lg text-sm font-semibold transition-colors"
                  >
                    {paying
                      ? <span className="inline-flex items-center gap-2 justify-center"><Loader2 size={14} className="animate-spin" /> {t('course.sending', 'Sending…')}</span>
                      : `${t('course.paySecurely', 'Pay Securely')} — £${formatPrice(activePrice, language)}${paymentType === 'installment' && perInstallment ? ' × 2' : ''}`}
                  </button>
                  <p className="text-xs text-gray-500">{t('course.paymentHelp', 'Secure checkout powered by Stripe.')}</p>
                  {paymentError && <p className="text-xs text-red-600">{paymentError}</p>}
                  <button
                    onClick={() => setShowEnrollForm(true)}
                    className="block w-full text-center border border-gray-200 text-gray-700 hover:bg-gray-50 py-3 rounded-lg text-sm font-medium transition-colors"
                  >
                    {t('course.requestCallback', 'Request a Callback')}
                  </button>
                </div>
              ) : submitted ? (
                <div className="p-5 pt-0">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle size={24} className="text-green-600 mx-auto mb-2" />
                    <p className="text-green-800 font-semibold text-sm">{t('course.enquirySubmitted', 'Enquiry Submitted!')}</p>
                    <p className="text-green-600 text-xs mt-1">{t('course.callbackSuccess', 'Our team will contact you within 24 hours to complete your enrolment.')}</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-5 pt-0 space-y-3">
                  <p className="text-sm font-semibold text-gray-800 mb-2">{t('course.callbackTitle', 'Request a Callback')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input required placeholder={t('course.firstName', 'First name')} value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                    <input required placeholder={t('course.lastName', 'Last name')} value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  </div>
                  <input required type="email" placeholder={t('course.emailAddress', 'Email address')} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  <input type="tel" dir="ltr" placeholder={t('course.phoneNumber', 'Phone number')} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                  {submitError && <p className="text-red-500 text-xs">{submitError}</p>}
                  <div className="flex gap-2">
                    <button type="submit" disabled={submitLoading} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2 rounded-lg text-xs font-bold transition-colors flex items-center justify-center gap-1">
                      {submitLoading ? <><Loader2 size={12} className="animate-spin" /> {t('course.sending', 'Sending…')}</> : t('course.submitEnquiry', 'Submit Enquiry')}
                    </button>
                    <button type="button" onClick={() => setShowEnrollForm(false)} className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50">
                      {t('course.cancel', 'Cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Related courses */}
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-5">{t('course.relatedCourses', 'Related Courses')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map(c => (
                <Link key={c.id} to={`/courses/${c.slug}`} className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-36 overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <span className="text-xs bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded">{c.level}</span>
                    <h3 className="font-semibold text-gray-900 text-sm mt-2 group-hover:text-blue-700 transition-colors line-clamp-2">{c.title}</h3>
                    <p className="text-blue-700 font-bold text-sm mt-2">£{formatPrice(c.price, language)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
